import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client';
import { Produto } from '../types';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '../utils/errorHandler';

export const useProdutos = (apenasAtivos = true) => {
  return useQuery({
    queryKey: ['produtos', apenasAtivos],
    queryFn: async () => {
      const response = await api.get<{ produtos: Produto[] }>(`/produtos?apenasAtivos=${apenasAtivos}`);
      return response.data.produtos;
    },
  });
};

export const useCriarProduto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (produto: Omit<Produto, 'id' | 'ativo' | 'dataCriacao' | 'dataUltimaAlteracao'>) => {
      const response = await api.post('/produtos', produto);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      toast.success('Produto criado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao criar produto'));
    },
  });
};

export const useAtualizarProduto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (produto: Produto) => {
      const { id, nome, descricao, precoUnitario, custo, tempoPreparoMinutos, sobEncomenda, ingredientes } = produto;
      const response = await api.put(`/produtos/${id}`, {
        nome,
        descricao,
        precoUnitario,
        custo,
        tempoPreparoMinutos,
        sobEncomenda,
        ingredientes,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      toast.success('Produto atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao atualizar produto'));
    },
  });
};

export const useUploadFotoProduto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, foto }: { id: number; foto: File }) => {
      const formData = new FormData();
      formData.append('foto', foto);
      const response = await api.post(`/produtos/${id}/foto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      toast.success('Foto atualizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao enviar foto'));
    },
  });
};

export const useAlternarStatusProduto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.patch(`/produtos/${id}/alternar-status`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
      toast.success('Status do produto alterado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(extractErrorMessage(error, 'Erro ao alterar status do produto'));
    },
  });
};
